import type { ComponentProps } from 'react'
import { useCallback, useMemo, useState } from 'react'

import { Tooltip } from '@/components/ui/tooltip.tsx'
import type { Inserter } from '@/modules/message/components/editor/fields/message-label-inserter'
import { MessageLabelInserter } from '@/modules/message/components/editor/fields/message-label-inserter'
import { PlaceholderSelector } from '@/modules/message/components/editor/fields/placeholder-selector'
import { variants } from '@/modules/message/components/placeholder/variants'

export const DefaultPlaceholderInserterPair = ({
  ...props
}: ComponentProps<'div'>) => {
  return (
    <MessageLabelInserter {...props}>
      {({ insert }) => (
        <>
          <CustomNameSelector
            key="select-custom-name"
            insert={insert}
          />
          <FanNameSelector
            key="select-fan-name"
            insert={insert}
          />
        </>
      )}
    </MessageLabelInserter>
  )
}

export const CustomNameSelector = ({ insert }: { insert: Inserter }) => {
  const [showCustomName, setShowCustomName] = useState(false)

  return (
    <Tooltip
      content="The name you have set for your fan, excluding emojis & numbers"
      contentClassName={showCustomName ? 'hidden' : ''}
    >
      <PlaceholderSelector
        key="select-custom-name"
        name="Custom Name"
        variants={variants.customNameOptions}
        onClick={insert}
        open={showCustomName}
        onOpenChange={setShowCustomName}
        testId="custom-name"
      />
    </Tooltip>
  )
}

export const FanNameSelector = ({ insert }: { insert: Inserter }) => {
  const [onlyFirst, setOnlyFirst] = useState(false)
  const fanNameVariants = useMemo(
    () => (onlyFirst ? variants.firstFanNameOptions : variants.fanNameOptions),
    [onlyFirst],
  )

  const handleToggleFirstName = useCallback(
    () => setOnlyFirst((state) => !state),
    [],
  )

  const [showFanName, setShowFanName] = useState(false)

  return (
    <Tooltip
      content="The name that the fan has set for themselves, excluding emojis & numbers"
      contentClassName={showFanName ? 'hidden' : ''}
    >
      <PlaceholderSelector
        name={onlyFirst ? 'First Fan Name' : 'Fan Name'}
        variants={fanNameVariants}
        onClick={insert}
        onToggle={handleToggleFirstName}
        toggleValue={onlyFirst}
        toggleName="Only First Fan Name"
        toggleTooltip="Only the first name that the fan has set themselves, excluding emojis & numbers"
        open={showFanName}
        onOpenChange={setShowFanName}
        testId="fan-name"
      />
    </Tooltip>
  )
}
